import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock, ExternalLink, CheckCircle2, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { dismissStaleInterviewPopup } from '../../lib/utils';
import axios from 'axios';

const StaleInterviewPopup = ({ isOpen, onClose, staleInterviews }) => {
  const navigate = useNavigate();
  const [isClosing, setIsClosing] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updatingButton, setUpdatingButton] = useState(null); // Track which specific button is loading
  const [updateSuccess, setUpdateSuccess] = useState({});
  const [updateError, setUpdateError] = useState('');
  const [completedInterviews, setCompletedInterviews] = useState(new Set());
  const [currentInterviewIndex, setCurrentInterviewIndex] = useState(0);
  const [showStillInterviewingOptions, setShowStillInterviewingOptions] = useState({}); // Track which interviews show sub-options
  const confettiCanvasRef = useRef(null);

  // Filter out completed interviews
  const remainingInterviews = staleInterviews?.filter(interview => 
    !completedInterviews.has(interview.id)
  ) || [];

  // Reset current index if it's out of bounds
  useEffect(() => {
    if (currentInterviewIndex >= remainingInterviews.length && remainingInterviews.length > 0) {
      setCurrentInterviewIndex(0);
    }
  }, [remainingInterviews.length, currentInterviewIndex]);

  if (!isOpen || !staleInterviews?.length) return null;

  // Get current interview to display
  const currentInterview = remainingInterviews[currentInterviewIndex];

  // Close modal if all interviews are completed
  if (remainingInterviews.length === 0 && staleInterviews.length > 0) {
    setTimeout(() => {
      handleClose();
    }, 1000); // Small delay to show success state
  }

  const handleClose = () => {
    setIsClosing(true);
    dismissStaleInterviewPopup();
    setShowStillInterviewingOptions({}); // Reset sub-options when closing
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 200);
  };



  // Confetti firing function
  const fireConfetti = async () => {
    const confetti = (await import('canvas-confetti')).default;
    const canvas = confettiCanvasRef.current;
    if (!canvas) return;
    
    // Create confetti instance with disabled worker to avoid canvas clone error
    const confettiInstance = confetti.create(canvas, {
      resize: true,
      useWorker: false, // Disable worker to avoid canvas clone error
    });
    
    // Fire from left bottom
    confettiInstance({
      particleCount: 60,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 1 },
      colors: ['#00C9A7', '#FFD166', '#FF61A6', '#2D9CDB'],
      ticks: 200,
      startVelocity: 45,
      scalar: 1.1,
      disableForReducedMotion: true,
    });
    
    // Fire from right bottom
    confettiInstance({
      particleCount: 60,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 1 },
      colors: ['#00C9A7', '#FFD166', '#FF61A6', '#2D9CDB'],
      ticks: 200,
      startVelocity: 45,
      scalar: 1.1,
      disableForReducedMotion: true,
    });
  };

  // Handle status update for individual interviews
  const handleStatusUpdate = async (interview, statusId) => {
    // If "still-interviewing" is clicked, show sub-options instead of updating immediately
    if (statusId === 'still-interviewing') {
      setShowStillInterviewingOptions(prev => ({
        ...prev,
        [interview.id]: true
      }));
      return;
    }
    
    setIsUpdating(true);
    setUpdatingButton(`${interview.id}-${statusId}`); // Track specific button
    setUpdateError('');
    
    try {
      const token = localStorage.getItem('token');
      
      const statusMap = {
        'first-interview': 'First interview',
        'multiple-interview-rounds': 'Passed the first round',
        'final-interview': 'Final interview',
        'offer-received': 'Received an offer (pending decision)',
        'offer-accepted': 'Accepted offer',
        'offer-rejected': 'Offer rejected',
        'rejected-at-interview': 'Rejected at interview',
        'candidate-withdrawn': 'Candidate withdrawn'
      };
      
      const updateData = {
        fields: {
          'Candidate Interview Stage': statusMap[statusId],
          'Updated on popup': true, // Track that this update came from the stale interview popup
          'Last Updated': new Date().toISOString() // Force timestamp update to prevent infinite popup loop
        },
        interviewId: interview.recordId || interview.id
      };
      
      console.log('Updating interview with data:', {
        interviewId: interview.recordId || interview.id,
        status: statusMap[statusId],
        timestamp: new Date().toISOString(),
        fields: updateData.fields
      });

      await axios.patch(`/api/interviews/updateInterview`, updateData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      // Mark this interview as successfully updated
      setUpdateSuccess(prev => ({
        ...prev,
        [interview.id]: statusMap[statusId]
      }));
      
      // Add to completed interviews so it disappears from the list
      setCompletedInterviews(prev => new Set([...prev, interview.id]));
      
      // Reset sub-options for this interview
      setShowStillInterviewingOptions(prev => ({
        ...prev,
        [interview.id]: false
      }));
      
      if (statusId === 'offer-received') {
        setTimeout(fireConfetti, 100); // slight delay to ensure canvas is mounted
      }

      // Auto-advance to next interview after a short delay
      setTimeout(() => {
        // If there are more interviews to show, don't change the index (it will auto-adjust)
        // The useEffect will handle index bounds
      }, 1000);
      
      // Clear any existing error
      setUpdateError('');
      
    } catch (error) {
      console.error('Error updating interview status:', error);
      setUpdateError('Failed to update status. Please try again.');
      setTimeout(() => setUpdateError(''), 3000);
    } finally {
      setIsUpdating(false);
      setUpdatingButton(null); // Clear specific button loading
    }
  };



  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          {/* Confetti Canvas */}
          <canvas
            ref={confettiCanvasRef}
            style={{
              position: 'fixed',
              pointerEvents: 'none',
              left: 0,
              top: 0,
              width: '100vw',
              height: '100vh',
              zIndex: 99999,
            }}
            width={typeof window !== 'undefined' ? window.innerWidth : 1920}
            height={typeof window !== 'undefined' ? window.innerHeight : 1080}
          />
                      <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.9 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="bg-white w-full max-w-2xl max-h-[90vh] rounded-2xl shadow-2xl overflow-visible"
          >
            {/* Header */}
            <div className="p-5 border-b border-gray-100">
              <div className="text-center">
                <h1 className="text-2xl font-bold text-gray-900 mb-1">Update Interview Status</h1>
                <p className="text-gray-600 text-base mb-2">
                  Interview <span className="font-semibold text-[#02B491]">{currentInterviewIndex + 1}</span> of <span className="font-semibold text-[#02B491]">{remainingInterviews.length}</span>
                </p>
                <p className="text-gray-500 text-sm">
                  Tell us how your interviews are going
                </p>
              </div>
            </div>

            {/* Content */}
            <div className="p-8 flex flex-col flex-1 overflow-hidden">
              {/* Error Message */}
              {updateError && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                  {updateError}
                </div>
              )}

              {/* Single Interview Display */}
              <div className="flex-1 flex items-center justify-center">
                {remainingInterviews.length === 0 && staleInterviews.length > 0 ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">All interviews updated!</h3>
                    <p className="text-gray-600 text-sm">Great job keeping your interview statuses current.</p>
                  </div>
                ) : currentInterview ? (
                  <div className="w-full max-w-lg">
                    <AnimatePresence mode="wait">
                      <motion.div 
                        key={currentInterview.id}
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -50 }}
                        transition={{ duration: 0.3 }}
                        className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm"
                      >
                        <div className="text-center mb-6">
                          <h3 className="font-bold text-gray-900 text-xl mb-2">
                            {currentInterview['Role Title'] || 'No Title'}
                          </h3>
                          <p className="text-gray-600 text-base mb-4">
                            {currentInterview['Name (from Startup)'] || 'Unknown Company'}
                          </p>
                          <div className="flex flex-wrap gap-2 justify-center">
                            {currentInterview['Location (from Startup)'] && (
                              <span className="text-gray-600 text-sm bg-gray-50 px-3 py-1 rounded-full">
                                📍 {currentInterview['Location (from Startup)']}
                              </span>
                            )}
                            {currentInterview['Compensation'] && (
                              <span className="text-gray-600 text-sm bg-gray-50 px-3 py-1 rounded-full">
                                💰 {currentInterview['Compensation']}
                              </span>
                            )}
                            {currentInterview['Funding Stage (from Startup)'] && (
                              <span className="text-gray-600 text-sm bg-gray-50 px-3 py-1 rounded-full">
                                🚀 {currentInterview['Funding Stage (from Startup)']}
                              </span>
                            )}
                            {currentInterview['Team Size (from Startup)'] && (
                              <span className="text-gray-600 text-sm bg-gray-50 px-3 py-1 rounded-full">
                                👥 {currentInterview['Team Size (from Startup)']} people
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Status Update Section */}
                        <div className="space-y-4">
                          <p className="text-sm text-gray-600 font-medium text-center">How did this interview go?</p>
                          
                          {/* Show interview stage sub-options if "Still interviewing" was clicked */}
                          {showStillInterviewingOptions[currentInterview.id] ? (
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <p className="text-sm text-gray-600 font-medium">Where are you in the interview process?</p>
                                <button
                                  onClick={() => setShowStillInterviewingOptions(prev => ({
                                    ...prev,
                                    [currentInterview.id]: false
                                  }))}
                                  className="text-sm text-gray-500 hover:text-gray-700 underline"
                                >
                                  Back
                                </button>
                              </div>
                              <div className="space-y-2">
                                <button
                                  onClick={() => handleStatusUpdate(currentInterview, 'first-interview')}
                                  disabled={isUpdating}
                                  className="w-full flex items-center justify-center gap-2 py-3 px-4 text-sm bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 hover:border-gray-400 active:bg-gray-100 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                                >
                                  <span className="font-medium">First interview</span>
                                  {updatingButton === `${currentInterview.id}-first-interview` && <Loader2 className="w-4 h-4 animate-spin ml-auto" />}
                                </button>
                                
                                <button
                                  onClick={() => handleStatusUpdate(currentInterview, 'multiple-interview-rounds')}
                                  disabled={isUpdating}
                                  className="w-full flex items-center justify-center gap-2 py-3 px-4 text-sm bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 hover:border-gray-400 active:bg-gray-100 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                                >
                                  <span className="font-medium">I passed the first round</span>
                                  {updatingButton === `${currentInterview.id}-multiple-interview-rounds` && <Loader2 className="w-4 h-4 animate-spin ml-auto" />}
                                </button>
                                
                                <button
                                  onClick={() => handleStatusUpdate(currentInterview, 'final-interview')}
                                  disabled={isUpdating}
                                  className="w-full flex items-center justify-center gap-2 py-3 px-4 text-sm bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 hover:border-gray-400 active:bg-gray-100 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                                >
                                  <span className="font-medium">Final interview</span>
                                  {updatingButton === `${currentInterview.id}-final-interview` && <Loader2 className="w-4 h-4 animate-spin ml-auto" />}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 gap-3">
                              <button
                                onClick={() => handleStatusUpdate(currentInterview, 'still-interviewing')}
                                disabled={isUpdating}
                                className="flex items-center justify-center gap-2 py-3 px-4 text-sm bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 hover:border-gray-400 active:bg-gray-100 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                              >
                                <span className="text-base">📝</span>
                                <span className="font-medium">Still interviewing</span>
                              </button>
                              
                              <button
                                onClick={() => handleStatusUpdate(currentInterview, 'offer-received')}
                                disabled={isUpdating}
                                className="flex items-center justify-center gap-2 py-3 px-4 text-sm bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 hover:border-gray-400 active:bg-gray-100 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                              >
                                <span className="text-base">🎉</span>
                                <span className="font-medium">Received offer</span>
                                {updatingButton === `${currentInterview.id}-offer-received` && <Loader2 className="w-4 h-4 animate-spin ml-auto" />}
                              </button>
                              
                              <button
                                onClick={() => handleStatusUpdate(currentInterview, 'rejected-at-interview')}
                                disabled={isUpdating}
                                className="flex items-center justify-center gap-2 py-3 px-4 text-sm bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 hover:border-gray-400 active:bg-gray-100 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                              >
                                <span className="text-base">❌</span>
                                <span className="font-medium">Rejected</span>
                                {updatingButton === `${currentInterview.id}-rejected-at-interview` && <Loader2 className="w-4 h-4 animate-spin ml-auto" />}
                              </button>
                              
                              <button
                                onClick={() => handleStatusUpdate(currentInterview, 'candidate-withdrawn')}
                                disabled={isUpdating}
                                className="flex items-center justify-center gap-2 py-3 px-4 text-sm bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 hover:border-gray-400 active:bg-gray-100 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                              >
                                <span className="text-base">🚪</span>
                                <span className="font-medium">I withdrew</span>
                                {updatingButton === `${currentInterview.id}-candidate-withdrawn` && <Loader2 className="w-4 h-4 animate-spin ml-auto" />}
                              </button>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    </AnimatePresence>
                  </div>
                ) : null}
              </div>
              
              {/* Contact Information */}
              <div className="mt-6 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-xs text-gray-600 text-center">
                  If you have been waiting for a response for more than 72 hours, email the Jumpstart team at{' '}
                  <a 
                    href="mailto:info@jumpstart-uk.com" 
                    className="text-[#02B491] hover:text-[#018268] font-medium underline"
                  >
                    info@jumpstart-uk.com
                  </a>
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default StaleInterviewPopup; 
//fefeffefefffwfwffefeffefknkjnbn